/**
 * Whether to offer the 32-bit Device Service build, and to whom.
 *
 * There are two Device Service installers and they are not interchangeable: the 64-bit build is what
 * nearly every workstation runs, and the 32-bit build exists for the scanners whose vendor libraries are
 * 32-bit only — the i3 DigID today. Handing the wrong one to a customer produces a service that installs
 * and then cannot talk to their scanner, which reads as a broken scanner rather than a wrong download.
 *
 * So it is shown only to customers who have selected a scanner it is FOR, never as a general download.
 * Which scanners those are comes from the item's own `deviceGroups` rather than a hardcoded name, so
 * attaching the build to another 32-bit device later is a catalog edit and not a code change.
 *
 * The rule fails CLOSED. An item with no device groups is not shown at all, because the failure it
 * prevents is asymmetric: a 32-bit customer who cannot find their build asks support and gets it in a
 * reply, while a 64-bit customer who installs it silently breaks a working workstation.
 */

export interface Build32Candidate {
  deviceGroups?: string[] | undefined;
}

/**
 * Which SINGLE Device Service build to offer, given the scanner the operator has selected.
 *
 * One or the other, never both. Showing both at once is worse than showing the wrong one: two Device
 * Services on a workstation contend for the same port, so a customer who obligingly installs everything on
 * the page ends up with a machine that is less functional than before they started.
 *
 * The 32-bit build wins for the scanners it is scoped to, because for those it is not an alternative — the
 * vendor libraries are 32-bit only and the 64-bit service cannot drive them at all.
 */
export function pickDeviceServiceBuild<T extends Build32Candidate>(
  latest64: T | undefined,
  latest32: T | undefined,
  activeGroup: string | null | undefined,
): { item: T | undefined; is32: boolean } {
  if (show32BitFor(latest32, activeGroup) && latest32) return { item: latest32, is32: true };
  return { item: latest64, is32: false };
}

export function show32BitFor(
  item: Build32Candidate | undefined,
  activeGroup: string | null | undefined,
): boolean {
  if (!item || !activeGroup) return false;
  const groups = item.deviceGroups ?? [];
  // No groups means "we were not told which scanners need this" — not "all of them".
  if (groups.length === 0) return false;
  return groups.includes(activeGroup);
}

/**
 * Whether the installed version is behind a given build.
 *
 * Compared against the SAME architecture's latest, which the global update banner cannot do: it reads the
 * 64-bit catalog entry, so once the 64-bit build passes the 32-bit one in version a 32-bit workstation is
 * told it is out of date and pointed at an installer it must not run. On this panel the comparison is
 * against the build the customer actually runs.
 */
export function isBehind(
  installed: string | null | undefined,
  available: string | undefined,
  cmp: (a: string, b: string) => number,
): boolean {
  if (!installed || !available) return false;
  return cmp(installed, available) < 0;
}
